/**
 * <marquee-strip> — the scrolling badge band.
 *
 * The scroll itself is a CSS animation that translates the track by exactly -50%, so it
 * works with JavaScript disabled and stops entirely under `prefers-reduced-motion`.
 *
 * For that -50% to loop seamlessly the track must hold an even number of identical
 * copies AND be at least twice the viewport wide — otherwise a short list on a wide
 * screen runs out of content and the loop visibly jumps. Liquid renders two copies;
 * this element clones more (always in pairs) until that holds, and re-checks on resize.
 *
 * It also pauses the scroll while a pointer is over the strip or something inside it has
 * keyboard focus, so the text can actually be read.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class MarqueeStrip extends HTMLElement {
    connectedCallback() {
      const speed = Number(this.dataset.speed);
      if (Number.isFinite(speed) && speed > 0) {
        this.style.setProperty('--strip-duration', `${speed}s`);
      }

      this.viewport = this.querySelector('[data-strip-viewport]');
      this.template = this.querySelector('[data-strip-track]');

      const pause = () => this.classList.add('is-paused');
      const resume = () => this.classList.remove('is-paused');

      this.addEventListener('pointerenter', pause);
      this.addEventListener('pointerleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);

      this.fill();

      // Fonts load after first paint and change the track width, so re-check then too.
      document.fonts?.ready.then(() => this.fill());

      this.resizeObserver = new ResizeObserver(() => this.fill());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    /** Clones the track in pairs until the strip is at least twice its own width. */
    fill() {
      if (!this.viewport || !this.template) return;

      const target = this.offsetWidth * 2;
      // Guard against an unbounded loop if the track somehow measures zero.
      const trackWidth = this.template.offsetWidth;
      if (trackWidth <= 0) return;

      let guard = 0;
      while (this.viewport.scrollWidth < target && guard < 20) {
        for (let i = 0; i < 2; i++) {
          const clone = this.template.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-strip-track');
          this.viewport.append(clone);
        }
        guard++;
      }
    }
  }

  if (!customElements.get('marquee-strip')) {
    customElements.define('marquee-strip', MarqueeStrip);
  }
})();
